import { CombinedGraphQLErrors } from '@apollo/client/errors';

export type UserError = {
  code: string;
  message: string;
  path?: readonly string[] | null;
};

export type FieldErrors = Record<string, string[]>;

const FIELD_ALIASES: Record<string, string> = {
  firstname: 'firstName',
  lastname: 'lastName',
};

const toCamelCase = (value: string) =>
  value.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());

const pathToFieldKey = (path?: readonly string[] | null) => {
  const key = path?.[path.length - 1];
  if (!key) return null;

  const normalized = toCamelCase(key);
  return FIELD_ALIASES[normalized] ?? normalized;
};

export const hasGraphQLErrorCode = (error: unknown, code: string): boolean => {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }

  return error.errors.some(
    (graphQLError) => graphQLError.extensions?.code === code
  );
};

export const buildFieldErrors = (
  errors?: readonly UserError[] | null
): FieldErrors => {
  if (!errors?.length) return {};

  return errors.reduce<FieldErrors>((acc, error) => {
    const key = pathToFieldKey(error.path);
    if (!key) return acc;

    acc[key] = [...(acc[key] ?? []), error.message];
    return acc;
  }, {});
};

export const buildGlobalErrors = (
  errors: readonly UserError[] | null | undefined,
  fieldKeys: readonly string[]
) => {
  if (!errors?.length) return [];

  const knownFieldKeys = new Set(fieldKeys);
  return errors.filter((error) => {
    const key = pathToFieldKey(error.path);
    return !key || !knownFieldKeys.has(key);
  });
};

export const mergeFieldErrors = (
  ...errorGroups: Array<FieldErrors | undefined>
) => {
  return errorGroups.reduce<FieldErrors>((acc, group) => {
    if (!group) return acc;

    Object.entries(group).forEach(([key, messages]) => {
      acc[key] = [...(acc[key] ?? []), ...messages];
    });

    return acc;
  }, {});
};
